<template>
  <v-form id="editTags" ref="editTags" v-model="formValid">
    <v-container fluid>
      <v-row>
        <table id="tagsTable">
          <tbody>
            <tr
              v-for="(section, sectionName, sectionIndex) in sections"
              :key="'edit_keywords_' + sectionIndex"
              class="mb-3"
              @click="showMenu()"
            >
              <td
                :class="'bg-' + section.color"
                class="text-white py-2 px-4 titleCell"
              >
                <v-tooltip location="right">
                  <template #activator="{ props }">
                    <v-icon class="text-white mr-1" size="small" v-bind="props">
                      fas fa-question-circle
                    </v-icon>
                  </template>
                  <span> {{ section.tooltip }} </span>
                </v-tooltip>
                {{ sectionName.toUpperCase() }}
              </td>
              <td :class="'bg-' + section.color + '_lighten_2'">
                <div class="pl-2">
                  <v-chip
                    v-for="(tag, tagIndex) in section.items"
                    :key="'section_' + sectionIndex + '_tag_' + tagIndex"
                    :class="[
                      !isNew(tag, sectionName)
                        ? 'bg-white'
                        : 'bg-' + section.color,
                    ]"
                    :color="!isNew(tag, sectionName) ? section.color : 'white'"
                    class="pr-3 my-1 mr-2 ml-0"
                    variant="outlined"
                    @click.stop
                  >
                    <KeywordTooltip :keyword="tag" />
                    <v-tooltip location="bottom">
                      <template #activator="{ props }">
                        <v-icon
                          :class="[
                            !isNew(tag, sectionName)
                              ? 'text-' + section.color
                              : 'text-white',
                          ]"
                          class="ml-1"
                          size="small"
                          v-bind="props"
                          @click="removeTag(section.items, tagIndex)"
                          @click.stop
                        >
                          fas fa-times-circle
                        </v-icon>
                      </template>
                      <span> Remove term </span>
                    </v-tooltip>
                  </v-chip>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </v-row>
      <v-row class="pr-5">
        <v-spacer />
        <v-chip
          :disabled="loading"
          class="text-white bg-green pr-5 ml-3 mb-3 shadowChip"
          @click="showMenu()"
        >
          <v-icon class="mr-3 text-white" size="small">
            {{ buttonIcon }}
          </v-icon>
          {{ buttonLabel }}
        </v-chip>
      </v-row>
    </v-container>

    <v-expand-transition class="ma-5">
      <v-container v-if="menu.show" class="py-0" fluid>
        <v-row justify="center" no-gutters>
          <v-col cols="12">
            <v-text-field
              id="searchString"
              v-model="searchString"
              append-inner-icon="fas fa-search"
              hide-details
              label="Search names and synonyms"
              variant="outlined"
            >
              <template #prepend>
                <v-menu :close-on-content-click="false">
                  <template #activator="{ props }">
                    <v-icon
                      class="text-blue ml-3 mr-1 opacity-100"
                      v-bind="props"
                    >
                      fas fa-cog
                    </v-icon>
                  </template>
                  <v-list>
                    <v-list-item>
                      <div class="d-flex align-center">
                        <v-list-item-action>
                          <v-switch
                            v-model="showTypes.taxonomy"
                            :color="colors.taxonomy"
                            class="mr-2"
                            hide-details
                            inset
                          />
                        </v-list-item-action>
                        <v-list-item-title
                          >Show taxonomic range
                        </v-list-item-title>
                      </div>
                    </v-list-item>
                    <v-divider class="my-1" opacity="80" />
                    <v-list-item>
                      <div class="d-flex align-center">
                        <v-list-item-action>
                          <v-switch
                            v-model="showTypes.subject"
                            :color="colors.subject"
                            class="mr-2"
                            hide-details
                            inset
                          />
                        </v-list-item-action>
                        <v-list-item-title>Show subjects</v-list-item-title>
                      </div>
                    </v-list-item>

                    <v-divider class="my-1" opacity="80" />
                    <v-list-item>
                      <div class="d-flex align-center">
                        <v-list-item-action>
                          <v-switch
                            v-model="showTypes.domain"
                            :color="colors.domain"
                            class="mr-2"
                            hide-details
                            inset
                          />
                        </v-list-item-action>
                        <v-list-item-title>Show domains</v-list-item-title>
                      </div>
                    </v-list-item>
                    <v-divider class="my-1" opacity="80" />
                    <v-list-item>
                      <div class="d-flex align-center">
                        <v-list-item-action>
                          <v-switch
                            v-model="showTypes.user_defined_tag"
                            :color="colors.user_defined_tag"
                            class="mr-2"
                            hide-details
                            inset
                          />
                        </v-list-item-action>
                        <v-list-item-title
                          >Show user defined tags
                        </v-list-item-title>
                      </div>
                    </v-list-item>
                    <v-divider class="my-1" opacity="80" />
                    <v-list-item>
                      <div class="d-flex align-center">
                        <v-list-item-action>
                          <v-switch
                            v-model="showTypes.object_type"
                            :color="colors.object_type"
                            class="mr-2"
                            hide-details
                            inset
                          />
                        </v-list-item-action>
                        <v-list-item-title>Show object types</v-list-item-title>
                      </div>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </template>
            </v-text-field>
            <v-data-table
              v-model="recordTags"
              v-model:search-input="searchString"
              :footer-props="{ 'items-per-page-options': [10, 20, 30, 40, 50] }"
              :headers="headers"
              :items="tags"
              :items-per-page="10"
              :loading="loading"
              calculate-widths
              class="elevation-1"
              item-key="label"
              loading-text="Please wait, tags are loading"
              mobile-breakpoint="900"
              return-object
              show-select
            >
              <template #[`item.model`]="{ item }">
                <div :class="'text-' + colors[item.model]" class="noBreak">
                  {{ item.model.toUpperCase().replace(/_/g, " ") }}
                </div>
              </template>
              <template #[`item.label`]="{ item }">
                <v-chip
                  :color="colors[item.model]"
                  class="text-white noBreak"
                  variant="flat"
                >
                  {{ capitaliseText(item.label, item.model) }}
                </v-chip>
              </template>
              <template #[`item.definitions`]="{ item }">
                <div v-if="item.definitions" class="text-start">
                  {{ item.definitions[0] }}
                </div>
              </template>
              <template #[`item.synonyms`]="{ item }">
                <div v-if="item.synonyms" class="text-start">
                  {{ item.synonyms.join(", ") }}
                </div>
              </template>
            </v-data-table>
          </v-col>
          <v-col cols="12">
            <new-tags />
          </v-col>
        </v-row>
      </v-container>
    </v-expand-transition>
  </v-form>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";

import KeywordTooltip from "@/components/Records/Shared/KeywordTooltip.vue";
import recordsCardUtils from "@/utils/recordsCardUtils";

import NewTags from "./NewTags";

export default {
  name: "EditTags",
  components: { NewTags, KeywordTooltip },
  mixins: [recordsCardUtils],
  data() {
    return {
      formValid: false,
      menu: {
        content: null,
        show: false,
        label: "Edit record's tags",
      },
      headers: [
        {
          title: "Type of keyword",
          sortable: false,
          value: "model",
        },
        {
          title: "Name",
          sortable: false,
          value: "label",
        },
        {
          title: "Definition",
          sortable: false,
          value: "definitions",
          filterable: false,
        },
        {
          title: "Alternative names",
          sortable: false,
          value: "synonyms",
        },
      ],
      searchString: null,
      initialized: false,
      showTypes: {
        domain: true,
        taxonomy: true,
        subject: true,
        user_defined_tag: true,
        object_type: true,
      },
      tags: [],
      loading: false,
      showAddTagOverlay: false,
      lastQuery: null,
    };
  },
  computed: {
    ...mapGetters("record", ["getSection"]),
    ...mapState("editor", ["tooltips", "colors"]),
    ...mapGetters("editor", ["getPartialTags"]),
    ...mapState("record", ["sections"]),
    sections() {
      return {
        "taxonomic range": {
          items: this.getSection("generalInformation").data.taxonomies,
          color: this.colors["taxonomy"],
          tooltip: this.tooltips.species,
        },
        subjects: {
          items: this.getSection("generalInformation").data.subjects,
          color: this.colors["subject"],
          tooltip: this.tooltips.subjects,
        },
        domains: {
          items: this.getSection("generalInformation").data.domains,
          color: this.colors["domain"],
          tooltip: this.tooltips.domains,
        },
        "user defined tags": {
          items: this.getSection("generalInformation").data.userDefinedTags,
          color: this.colors["user_defined_tag"],
          tooltip: this.tooltips.userDefinedTags,
        },
        "object types": {
          items: this.getSection("generalInformation").data.objectTypes,
          color: this.colors["object_type"],
          tooltip: this.tooltips.objectTypes,
        },
      };
    },
    buttonLabel() {
      if (this.menu.show) return "Hide table";
      return "Add/edit tags";
    },
    buttonIcon() {
      if (this.menu.show) return "fas fa-minus-circle";
      return "fas fa-plus-circle";
    },
    recordTags: {
      get() {
        return this.getSection("generalInformation")
          .data.taxonomies.map((term) => {
            term.model = "taxonomy";
            return term;
          })
          .concat(
            this.getSection("generalInformation").data.domains.map((term) => {
              term.model = "domain";
              return term;
            }),
          )
          .concat(
            this.getSection("generalInformation").data.subjects.map((term) => {
              term.model = "subject";
              return term;
            }),
          )
          .concat(
            this.getSection("generalInformation").data.objectTypes.map(
              (term) => {
                term.model = "object_type";
                return term;
              },
            ),
          )
          .concat(
            this.getSection("generalInformation").data.userDefinedTags.map(
              (term) => {
                term.model = "user_defined_tag";
                return term;
              },
            ),
          );
      },
      set(val) {
        if (this.initialized) {
          let tags = {
            domain: [],
            taxonomy: [],
            subject: [],
            user_defined_tag: [],
            object_type: [],
          };
          for (let selectedTag of val) {
            tags[selectedTag.model].push(selectedTag);
          }
          this.$store.commit("record/setTags", {
            value: tags.domain,
            target: "domains",
          });
          this.$store.commit("record/setTags", {
            value: tags.taxonomy,
            target: "taxonomies",
          });
          this.$store.commit("record/setTags", {
            value: tags.subject,
            target: "subjects",
          });
          this.$store.commit("record/setTags", {
            value: tags.user_defined_tag,
            target: "userDefinedTags",
          });
          this.$store.commit("record/setTags", {
            value: tags.object_type,
            target: "objectTypes",
          });
        }
      },
    },
    initialSections() {
      return {
        "taxonomic range":
          this.getSection("generalInformation").initialData.taxonomies,
        subjects: this.getSection("generalInformation").initialData.subjects,
        domains: this.getSection("generalInformation").initialData.domains,
        "user defined tags":
          this.getSection("generalInformation").initialData.userDefinedTags,
        "object types":
          this.getSection("generalInformation").initialData.objectTypes,
      };
    },
  },
  watch: {
    async searchString(val) {
      this.loading = true;
      this.tags = [];
      val = val.trim();
      this.lastQuery = val;
      await this.getTags(val);
      /* istanbul ignore else */
      if (val === this.lastQuery) {
        this.partialTags();
        this.loading = false;
        this.$scrollTo("#editTags");
      }
    },
    showTypes: {
      deep: true,
      handler() {
        this.partialTags();
      },
    },
  },
  mounted() {
    this.$nextTick(async function () {
      this.loading = true;
      this.initialized = false;
      this.partialTags();
      this.loading = false;
      this.initialized = true;
    });
  },
  methods: {
    ...mapActions("editor", ["getTags"]),
    showMenu() {
      if (!this.menu.show) {
        this.$scrollTo("#editTags");
      }
      this.menu.show = !this.menu.show;
    },
    removeTag(sectionItems, termIndex) {
      sectionItems.splice(termIndex, 1);
    },
    partialTags() {
      let sections = [];
      Object.keys(this.showTypes).forEach((type) => {
        if (this.showTypes[type]) sections.push(type);
      });
      this.tags = this.getPartialTags(sections);
    },
    isNew(term, section) {
      return !this.initialSections[section].filter(
        (obj) => obj.id === term.id,
      )[0];
    },
  },
};
</script>

<style scoped>
table#tagsTable {
  border-collapse: collapse;
  width: 100%;
}

table#tagsTable td.titleCell {
  width: 100px;
  white-space: nowrap;
  text-align: center;
  font-size: 16px;
  font-weight: bolder;
  height: 55px;
}

table#tagsTable tr {
  border-collapse: collapse;
  border-bottom: 10px solid white;
}

.noBreak {
  white-space: nowrap;
}

.limitWidth {
  max-width: 800px;
}

.whiteBorder {
  border: 1px solid white !important;
}

:deep(.v-switch__track) {
  opacity: 1;
}
</style>
